// Type4 Mock Data - School View
// This is for School View (YearGroupTimetableGrid)
// School: Hollins - Year Groups: 7, 8, 9, 10, 11
// Cell shows: Subject, Class/Set, Room
// Popup shows: Subject and Class (title), Teacher, Room (and capacity), Period/Lesson Date, Period/Lesson Timing

export interface YearCellData {
  id: string;
  label: string;
  color: string;
  isUnavailable?: boolean;
  popupData: {
    title: string;
    subtitle?: string;
    details: { label: string; value: string }[];
  };
}

export interface PeriodInfo {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  type: 'lesson' | 'registration' | 'break' | 'lunch';
}

export interface YearGroupData {
  id: string;
  name: string;
  color: string; // Year group header color
  periods: PeriodInfo[];
  // Cells indexed by period-week-day
  cells: Record<string, YearCellData | null>;
}

export interface Type4Data {
  yearGroups: YearGroupData[];
  weeks: ('A' | 'B')[];
  days: string[];
}

// Subject colors - each subject has a unique color
const subjectColors: Record<string, string> = {
  Eng: '#10B981', // Green
  Mat: '#3B82F6', // Blue
  Sci: '#8B5CF6', // Purple
  DT: '#F59E0B', // Amber
  His: '#EF4444', // Red
  MFL: '#0EA5E9', // Sky
  Geo: '#EC4899', // Pink
  Art: '#6366F1', // Indigo
};

// Full subject names
const subjectFullNames: Record<string, string> = {
  Eng: 'English',
  Mat: 'Mathematics',
  Sci: 'Science',
  DT: 'Design Technology',
  His: 'History',
  MFL: 'Modern Foreign Languages',
  Geo: 'Geography',
  Art: 'Art',
};

// Year group colors (5 distinct colors)
const yearGroupColors: Record<number, string> = {
  7: '#DBEAFE', // Light blue
  8: '#D1FAE5', // Light green
  9: '#FEE2E2', // Light red
  10: '#F3E8FF', // Light purple
  11: '#FEF3C7', // Light yellow
};

// Teachers by subject with full names
interface TeacherInfo {
  initials: string;
  fullName: string;
  subject: string;
}

const allTeachers: TeacherInfo[] = [
  // English Teachers
  { initials: 'SHW', fullName: 'Mr S Howard', subject: 'Eng' },
  { initials: 'NTA', fullName: 'Ms N Taylor', subject: 'Eng' },
  { initials: 'SWA', fullName: 'Mrs S Walsh', subject: 'Eng' },
  // Maths Teachers
  { initials: 'JGA', fullName: 'Mr J Garcia', subject: 'Mat' },
  { initials: 'NBR', fullName: 'Mrs N Brown', subject: 'Mat' },
  { initials: 'DHA', fullName: 'Mr D Hall', subject: 'Mat' },
  // Science Teachers
  { initials: 'ECA', fullName: 'Dr E Carter', subject: 'Sci' },
  { initials: 'SBM', fullName: 'Mr S Bloom', subject: 'Sci' },
  // Design Teachers
  { initials: 'SSC', fullName: 'Mr S Scott', subject: 'DT' },
  { initials: 'EBA', fullName: 'Ms E Baker', subject: 'DT' },
  // History Teachers
  { initials: 'CWA', fullName: 'Mr C Watson', subject: 'His' },
  { initials: 'ASA', fullName: 'Ms A Sanders', subject: 'His' },
  // MFL Teachers
  { initials: 'CKI', fullName: 'Mrs C King', subject: 'MFL' },
  { initials: 'JGR', fullName: 'Mr J Green', subject: 'MFL' },
  { initials: 'HDO', fullName: 'Ms H Donovan', subject: 'MFL' },
  // Geography Teachers
  { initials: 'TDU', fullName: 'Mr T Duncan', subject: 'Geo' },
  { initials: 'NJO', fullName: 'Mrs N Jones', subject: 'Geo' },
  { initials: 'HUM', fullName: 'Dr H Humphrey', subject: 'Geo' },
  // Art Teachers
  { initials: 'LKE', fullName: 'Ms L Kelly', subject: 'Art' },
  { initials: 'PDR', fullName: 'Mr P Drew', subject: 'Art' },
  { initials: 'LWH', fullName: 'Mrs L White', subject: 'Art' },
];

// All rooms with capacities
interface RoomInfo {
  name: string;
  capacity: number;
}

const allRooms: RoomInfo[] = [
  { name: 'GYM', capacity: 120 },
  { name: 'Hall', capacity: 200 },
  { name: 'LRC', capacity: 60 },
  ...Array.from({ length: 30 }, (_, i) => ({
    name: `R${i + 1}`,
    capacity: 25 + Math.floor((i % 4) * 5), // Capacity varies between 25-40
  })),
];

// Subjects array
const subjects = Object.keys(subjectColors);

// Year groups: 7, 8, 9, 10, 11
const yearGroups = [7, 8, 9, 10, 11];

// Bands and Sets
const bandXSets789 = ['H', 'O', 'L'];
const bandYSets789 = ['I', 'N', 'S'];
const bandXSets1011 = ['H', 'O', 'L', 'T'];
const bandYSets1011 = ['I', 'N', 'S', 'C'];

// Dates for January 2026
const weekDates: Record<string, Record<string, string>> = {
  A: {
    Monday: 'Mon 5th Jan 2026',
    Tuesday: 'Tue 6th Jan 2026',
    Wednesday: 'Wed 7th Jan 2026',
    Thursday: 'Thu 8th Jan 2026',
    Friday: 'Fri 9th Jan 2026',
  },
  B: {
    Monday: 'Mon 12th Jan 2026',
    Tuesday: 'Tue 13th Jan 2026',
    Wednesday: 'Wed 14th Jan 2026',
    Thursday: 'Thu 15th Jan 2026',
    Friday: 'Fri 16th Jan 2026',
  },
};

// Seeded random for consistency
const seededRandom = (seed: number) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

// Get a set letter based on year group and seed
const getSetLetter = (yearGroup: number, seed: number): string => {
  if (yearGroup <= 9) {
    const allSets = [...bandXSets789, ...bandYSets789];
    return allSets[Math.floor(seededRandom(seed) * allSets.length)];
  }
  const allSets = [...bandXSets1011, ...bandYSets1011];
  return allSets[Math.floor(seededRandom(seed) * allSets.length)];
};

const standardPeriods: PeriodInfo[] = [
  { id: 'reg', name: 'Registration', startTime: '08.45', endTime: '09.00', type: 'registration' },
  { id: 'p1', name: '1', startTime: '09.05', endTime: '10.05', type: 'lesson' },
  { id: 'p2', name: '2', startTime: '10.05', endTime: '11.05', type: 'lesson' },
  { id: 'brk', name: 'Break', startTime: '11.05', endTime: '11.20', type: 'break' },
  { id: 'p3', name: '3', startTime: '11.20', endTime: '12.20', type: 'lesson' },
  { id: 'lun', name: 'Lunch', startTime: '12.20', endTime: '13.00', type: 'lunch' },
  { id: 'p4', name: '4', startTime: '13.00', endTime: '14.00', type: 'lesson' },
  { id: 'p5', name: '5', startTime: '14.00', endTime: '15.00', type: 'lesson' },
];

// Create cell for School View
// Cell shows: Subject, Class/Set, Room
// Popup shows: Subject and Class (title), Teacher, Room (and capacity), Period/Lesson Date, Period/Lesson Timing
const createYearCell = (
  id: string,
  subject: string,
  yearGroup: number,
  setLetter: string,
  room: RoomInfo,
  teacher: TeacherInfo,
  week: 'A' | 'B',
  day: string,
  period: PeriodInfo,
  isUnavailable = false,
): YearCellData => {
  const subjectCode = subject;
  const subjectName = subjectFullNames[subject] || subject;
  const color = subjectColors[subject] || '#94A3B8';
  const date = weekDates[week][day];

  // Cell label for School View: Subject, Class/Set, Room
  const label = `${subjectCode}\n${yearGroup}${setLetter}\n${room.name}`;

  return {
    id,
    label,
    color,
    isUnavailable,
    popupData: {
      title: `${subjectName} ${yearGroup}${setLetter}`,
      subtitle: subjectCode,
      details: [
        { label: 'Teacher', value: teacher.fullName },
        { label: 'Room', value: `${room.name} (Capacity: ${room.capacity})` },
        { label: 'Date', value: date },
        { label: 'Time', value: `${period.startTime} - ${period.endTime}` },
      ],
    },
  };
};

const generateYearGroups = (): YearGroupData[] => {
  const weeks: ('A' | 'B')[] = ['A', 'B'];
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  return yearGroups.map((yearNum, yearIndex) => {
    const cells: Record<string, YearCellData | null> = {};

    standardPeriods.forEach((period, periodIndex) => {
      weeks.forEach((week, weekIndex) => {
        days.forEach((day, dayIndex) => {
          const key = `${period.id}-week${week}-${day.toLowerCase()}`;

          // Non-lesson periods don't have cells (they show the period type)
          if (period.type !== 'lesson') {
            cells[key] = null;
            return;
          }

          const seed = yearIndex * 10000 + weekIndex * 1000 + dayIndex * 100 + periodIndex;

          // Randomly determine if this cell has a lesson (85% chance)
          if (seededRandom(seed) > 0.15) {
            const subjectIndex = Math.floor(seededRandom(seed + 1) * subjects.length);
            const subject = subjects[subjectIndex];
            const setLetter = getSetLetter(yearNum, seed + 2);
            const room = allRooms[Math.floor(seededRandom(seed + 3) * allRooms.length)];

            // Get a teacher for this subject
            const subjectTeachers = allTeachers.filter((t) => t.subject === subject);
            const teacher = subjectTeachers[Math.floor(seededRandom(seed + 4) * subjectTeachers.length)];

            cells[key] = createYearCell(
              `year${yearNum}-${key}`,
              subject,
              yearNum,
              setLetter,
              room,
              teacher,
              week,
              day,
              period,
              // Mark some as unavailable (about 3%)
              seededRandom(seed + 5) < 0.03,
            );
          } else {
            cells[key] = null;
          }
        });
      });
    });

    return {
      id: `year-${yearNum}`,
      name: `Year ${yearNum}`,
      color: yearGroupColors[yearNum],
      periods: standardPeriods,
      cells,
    };
  });
};

export const type4MockData: Type4Data = {
  yearGroups: generateYearGroups(),
  weeks: ['A', 'B'],
  days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
};

export default type4MockData;
